import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { classesApi } from '@/api'
import type { ClassDetails } from '@/api/api'

export type ClassSort = 'name' | 'year' | 'size'
export type SidebarTheme = 'light' | 'ink' | 'indigo'

/**
 * Cross-cutting shell state shared by the app shell (§02) and views.
 *
 *  - `classSort` is shared between the rail "My classes" list and the §04
 *    overview grid. The control lives only on the grid; the rail never sorts.
 *  - `sidebarTheme` recolours only the rail (§01 --sidebar-* tokens).
 *  - `classes` is loaded once and reused by the rail and the palette.
 *
 * `classSort`, `sidebarTheme` and the My-classes collapse state persist
 * (pinia-plugin-persistedstate, wired in main.ts).
 */
export const useAppShellStore = defineStore(
  'appShell',
  () => {
    const classes = ref<ClassDetails[]>([])
    const classesLoaded = ref(false)
    const classesLoading = ref(false)

    const classSort = ref<ClassSort>('name')
    const sidebarTheme = ref<SidebarTheme>('light')
    const myClassesOpen = ref(true)
    const paletteOpen = ref(false)

    /** Leading year-group number in a class name ("8 Oasis" → 8), else Infinity. */
    function yearGroup(name: string): number {
      const m = name.match(/^\s*(\d+)/)
      return m && m[1] ? parseInt(m[1], 10) : Number.POSITIVE_INFINITY
    }

    /** Natural compare so "8 Oasis" precedes "10 Cypress". */
    function byNameNatural(a: ClassDetails, b: ClassDetails): number {
      return (a.name ?? '').localeCompare(b.name ?? '', undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    }

    const sortedClasses = computed<ClassDetails[]>(() => {
      const list = [...classes.value]
      switch (classSort.value) {
        case 'size':
          return list.sort(
            (a, b) => (b.studentCount ?? 0) - (a.studentCount ?? 0) || byNameNatural(a, b),
          )
        case 'year':
          return list.sort(
            (a, b) =>
              yearGroup(a.name ?? '') - yearGroup(b.name ?? '') || byNameNatural(a, b),
          )
        case 'name':
        default:
          return list.sort(byNameNatural)
      }
    })

    async function loadClasses(force = false) {
      if (classesLoading.value) return
      if (classesLoaded.value && !force) return
      classesLoading.value = true
      try {
        const res = await classesApi.classesGet()
        classes.value = (res.data ?? []) as ClassDetails[]
        classesLoaded.value = true
      } catch {
        // Leave existing list; rail simply shows what it has.
      } finally {
        classesLoading.value = false
      }
    }

    function setClassSort(sort: ClassSort) {
      classSort.value = sort
    }
    function setSidebarTheme(theme: SidebarTheme) {
      sidebarTheme.value = theme
    }
    function toggleMyClasses() {
      myClassesOpen.value = !myClassesOpen.value
    }
    function openPalette() {
      paletteOpen.value = true
    }
    function closePalette() {
      paletteOpen.value = false
    }
    function togglePalette() {
      paletteOpen.value = !paletteOpen.value
    }

    return {
      classes,
      classesLoaded,
      classesLoading,
      classSort,
      sidebarTheme,
      myClassesOpen,
      paletteOpen,
      sortedClasses,
      loadClasses,
      setClassSort,
      setSidebarTheme,
      toggleMyClasses,
      openPalette,
      closePalette,
      togglePalette,
    }
  },
  {
    persist: {
      pick: ['classSort', 'sidebarTheme', 'myClassesOpen'],
    },
  },
)
